import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { supabase } from "../supabase/client";
import { userErrorMessage } from "../utils/userErrorMessage";
import { useAuth } from "../components/AuthProvider";
import "../App.css";

const paidPlans = [
  { id: "monthly", title: "월간 선불", regular: 49900, price: 29900 },
  { id: "annual", title: "연간 선불", regular: 598800, price: 300000 },
];
const won = (amount) => `${amount.toLocaleString("ko-KR")}원`;

async function errorText(error) {
  const response = await error?.context?.clone?.().json().catch(() => null);
  return userErrorMessage(response?.error || error);
}

function readCheckout() {
  try { return JSON.parse(sessionStorage.getItem("busHubSignupCheckout") || "null"); }
  catch { return null; }
}

export default function SubscriptionCheckout() {
  const { user } = useAuth();
  const [plan, setPlan] = useState("trial");
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [context, setContext] = useState({ loading: true, trialAvailable: true, trialUsed: false });
  const checkout = useMemo(readCheckout, []);
  const hasRequestContext = !!checkout?.requestId || !!user;
  const selected = paidPlans.find((item) => item.id === plan);

  useEffect(() => {
    if (!hasRequestContext) {
      setContext({ loading: false, trialAvailable: false, trialUsed: false });
      return;
    }
    let active = true;
    supabase.functions.invoke("signup-subscription", {
      body: { action: "status", ...(checkout || {}) },
    }).then(async ({ data, error }) => {
      if (error) throw new Error(await errorText(error));
      if (!active) return;
      const trialAvailable = !!data?.trialAvailable && !!checkout;
      setContext({ loading: false, trialAvailable, trialUsed: !trialAvailable, ...data });
      if (!trialAvailable) setPlan("monthly");
    }).catch((error) => {
      if (!active) return;
      setContext((current) => ({ ...current, loading: false, trialAvailable: false }));
      setPlan("monthly");
      setErrorMessage(userErrorMessage(error));
    });
    return () => { active = false; };
  }, [checkout, hasRequestContext]);

  const requestTrial = async () => {
    if (!checkout?.requestId || !checkout?.checkoutToken || !context.trialAvailable || busy) return;
    setBusy(true);
    setErrorMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("signup-subscription", {
        body: { action: "request_trial", ...checkout },
      });
      if (error || !data?.ok) throw error || new Error("무료체험 신청을 완료하지 못했습니다.");
      sessionStorage.removeItem("busHubSignupCheckout");
      setSubmitted(true);
    } catch (error) {
      setErrorMessage(await errorText(error));
    } finally {
      setBusy(false);
    }
  };

  const startPayment = async () => {
    if (!selected || !hasRequestContext || !agreed || busy) return;
    setBusy(true);
    setErrorMessage("");
    try {
      const { data, error } = await supabase.functions.invoke("signup-subscription", {
        body: { action: "prepare", ...(checkout || {}), plan },
      });
      if (error) throw error;
      if (data?.amount !== selected.price) throw new Error("표시된 금액과 결제 금액이 다릅니다. 고객센터에 문의해 주세요.");
      sessionStorage.setItem("busHubSubscriptionPlan", plan);
      const toss = await loadTossPayments(data.clientKey);
      const payment = toss.payment({ customerKey: data.customerKey });
      await payment.requestBillingAuth({
        method: "CARD",
        successUrl: `${window.location.origin}/subscription/success`,
        failUrl: `${window.location.origin}/subscription/fail`,
        customerName: data.customerName,
        customerEmail: data.customerEmail,
      });
    } catch (error) {
      setErrorMessage(await errorText(error));
      setBusy(false);
    }
  };

  const paidDetail = (item) => {
    if (context.trialUsed) {
      return item.id === "annual"
        ? "오늘 300,000원을 결제하고 첫 결제일부터 12개월 이용합니다. 이 기간 중 추가 결제는 없습니다."
        : "오늘 29,900원을 결제하고 첫 결제일부터 12개월 이용합니다. 이후 월마다 11회 추가 결제됩니다.";
    }
    return item.id === "annual"
      ? "첫 달은 무료이며, 오늘 300,000원은 이후 12개월의 선결제입니다. 총 13개월 이용하며 이 기간 중 추가 결제는 없습니다."
      : "첫 달은 무료이며, 오늘 29,900원은 다음 유료 이용월의 첫 회차 선결제입니다. 2개월 뒤부터 월마다 11회 추가 결제되어 총 13개월 이용합니다.";
  };

  if (submitted) {
    return (
      <div className="login-background min-h-screen px-4 py-8">
        <main className="mx-auto max-w-xl rounded-md bg-white p-6 shadow-md">
          <h1 className="text-xl font-bold">한 달 무료 신청 완료</h1>
          <p role="status" className="mt-3 text-sm leading-6 text-gray-700">
            회원 가입 신청이 접수되었습니다. 운영자가 승인한 날부터 한 달 동안 BUS HUB를 무료로 이용할 수 있습니다.
          </p>
          <Link to="/login" className="mt-5 inline-block rounded-md bg-blue-700 px-5 py-2 text-white">로그인으로 돌아가기</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="login-background min-h-screen px-4 py-8">
      <main className="mx-auto max-w-5xl rounded-md bg-white p-5 shadow-md sm:p-7">
        <h1 className="text-xl font-bold">BUS HUB 이용권</h1>
        <p className="mt-2 text-sm text-gray-700">
          {context.trialUsed
            ? "무료 이용기간이 종료되었습니다. 요금제를 결제하면 BUS HUB를 다시 정상적으로 이용할 수 있습니다."
            : "한 달 무료 또는 유료 요금제를 선택합니다. 회원 가입 승인 후 이용할 수 있습니다."}
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <label className={`block rounded-md border p-4 ${context.trialAvailable ? "cursor-pointer" : "cursor-not-allowed bg-gray-100 text-gray-400"} ${plan === "trial" ? "border-blue-600 bg-blue-50" : "border-gray-300"}`}>
            <span className="flex items-center gap-2 font-semibold">
              <input type="radio" name="subscription-plan" value="trial" checked={plan === "trial"}
                disabled={!context.trialAvailable || context.loading} onChange={() => setPlan("trial")} />
              한 달 무료
            </span>
            <strong className="mt-7 block text-2xl text-blue-700">0원</strong>
            <span className="mt-2 block text-sm leading-6">운영자 승인일부터 한 달 동안 모든 기능을 이용할 수 있습니다.</span>
            {!context.trialAvailable && <span className="mt-2 block text-xs text-red-600">이미 무료체험을 사용한 계정은 다시 선택할 수 없습니다.</span>}
          </label>
          {paidPlans.map((item) => (
            <label key={item.id} className={`block cursor-pointer rounded-md border p-4 ${plan === item.id ? "border-blue-600 bg-blue-50" : "border-gray-300"}`}>
              <span className="flex items-center gap-2 font-semibold">
                <input type="radio" name="subscription-plan" value={item.id} checked={plan === item.id}
                  disabled={context.loading} onChange={() => setPlan(item.id)} />
                {item.title}
              </span>
              <span className="mt-3 block text-sm text-gray-500 line-through">정상가 {won(item.regular)}</span>
              <strong className="block text-2xl text-blue-700">{won(item.price)}</strong>
              <span className="mt-2 block text-sm leading-6 text-gray-700">{paidDetail(item)}</span>
            </label>
          ))}
        </div>
        {plan === "trial" ? (
          <p className="mt-4 text-sm text-gray-700">선택: 한 달 무료 · 오늘 결제 0원 · 운영자 승인일부터 한 달 이용</p>
        ) : (
          <>
            <p className="mt-4 text-sm text-gray-700">
              선택: {selected?.title} · 오늘 결제 {won(selected?.price || 0)} · 첫 결제일부터 {context.trialUsed ? "12개월" : "13개월"} 이용
            </p>
            <p className="mt-2 text-sm text-amber-800">현재 카드 정기결제만 지원합니다. 결제수단을 등록하면 선택한 금액이 즉시 청구됩니다.</p>
            <label className="mt-4 flex items-start gap-2 text-sm">
              <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
              <span>금액, 결제 주기, 제공기간 및 <a href="https://morbit.co.kr/refund/" target="_blank" rel="noreferrer" className="text-blue-700 underline">환불정책</a>을 확인했고 결제에 동의합니다.</span>
            </label>
          </>
        )}
        {errorMessage && <p role="alert" className="mt-3 text-sm text-red-700">{errorMessage}</p>}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          {hasRequestContext ? (
            plan === "trial" ? (
              <button type="button" onClick={requestTrial} disabled={!context.trialAvailable || context.loading || busy}
                className="rounded-md bg-blue-700 px-5 py-2 text-white disabled:opacity-50">
                {busy ? "신청 중..." : "신청"}
              </button>
            ) : (
              <button type="button" onClick={startPayment} disabled={!agreed || context.loading || busy}
                className="rounded-md bg-blue-700 px-5 py-2 text-white disabled:opacity-50">
                {busy ? "결제 준비 중..." : `${won(selected?.price || 0)} 결제하기`}
              </button>
            )
          ) : <Link to="/signup-request" className="rounded-md bg-blue-700 px-5 py-2 text-white">회원 가입 신청</Link>}
          <Link to="/login" className="text-sm text-blue-700">로그인으로 돌아가기</Link>
        </div>
      </main>
    </div>
  );
}
